import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_TIME_CONTROL_CONFIG,
  buildUrlWithTemplate,
  getOptionValue,
  normalizeString,
  normalizeTimeControlConfig,
} from '../utils/timeControlUtils';

export const useTimeControlApi = (timeControlConfig) => {
  const timeControlSettings = useMemo(
    () => {
      const conf = normalizeTimeControlConfig(timeControlConfig);
      console.log('[useTimeControlApi] Normalized config:', conf);
      return conf;
    },
    [timeControlConfig]
  );

  const {
    enabled: timeControlEnabled,
    rallyListUrl,
    controlPointsUrlTemplate,
    timesUrlTemplate,
    defaultRallyIds = [],
    refreshIntervalMs,
  } = timeControlSettings;

  const defaultRallyIdsKey = useMemo(() => {
    if (!Array.isArray(defaultRallyIds) || defaultRallyIds.length === 0) {
      return '';
    }
    return defaultRallyIds
      .map((id) => normalizeString(id))
      .filter(Boolean)
      .join('|');
  }, [defaultRallyIds]);

  const normalizedDefaultRallyIds = useMemo(() => {
    if (!defaultRallyIdsKey) {
      return [];
    }
    return defaultRallyIdsKey.split('|');
  }, [defaultRallyIdsKey]);

  const [rallyOptions, setRallyOptions] = useState([]);
  const [rallyLoading, setRallyLoading] = useState(false);
  const [rallyError, setRallyError] = useState('');
  const [selectedRally, setSelectedRally] = useState('');

  const [rallyPoints, setRallyPoints] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsError, setPointsError] = useState('');
  const [selectedRallyPoint, setSelectedRallyPoint] = useState('');

  const [pointTimes, setPointTimes] = useState([]);
  const [timesLoading, setTimesLoading] = useState(false);
  const [timesError, setTimesError] = useState('');

  useEffect(() => {
    if (!timeControlEnabled) {
      setRallyOptions([]);
      setSelectedRally('');
      setRallyError('');
      setRallyLoading(false);
      return;
    }

    const listUrl = normalizeString(rallyListUrl);
    if (!listUrl) {
      setRallyOptions([]);
      setSelectedRally('');
      setRallyError('');
      setRallyLoading(false);
      return;
    }

    let isActive = true;
    const controller = new AbortController();

    const fetchRallies = async () => {
      setRallyLoading(true);
      setRallyError('');
      try {
        console.log(`[useTimeControlApi] Fetching rallies from: ${listUrl}`);
        const response = await fetch(listUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (!isActive) return;
        console.log('[useTimeControlApi] Rallies data:', data);
        const normalizedData = Array.isArray(data) ? data : [];
        const allowedSet = new Set(normalizedDefaultRallyIds);
        const filteredData = allowedSet.size
          ? normalizedData.filter((item) => allowedSet.has(getOptionValue(item)))
          : normalizedData;
        setRallyOptions(filteredData);
        setSelectedRally((prev) => {
          if (prev && filteredData.some((item) => getOptionValue(item) === prev)) {
            return prev;
          }
          const prioritizedDefault = normalizedDefaultRallyIds
            .map((id) => filteredData.find((item) => getOptionValue(item) === id))
            .find(Boolean);
          const defaultOption = prioritizedDefault || filteredData[0];
          return defaultOption ? getOptionValue(defaultOption) : '';
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Failed to load rally dropdown options:', error);
        if (isActive) {
          setRallyOptions([]);
          setSelectedRally('');
          setRallyError('Failed to load rally list.');
        }
      } finally {
        if (isActive) {
          setRallyLoading(false);
        }
      }
    };

    fetchRallies();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [timeControlEnabled, rallyListUrl, normalizedDefaultRallyIds, defaultRallyIdsKey]);

  useEffect(() => {
    if (!timeControlEnabled) {
      setRallyPoints([]);
      setSelectedRallyPoint('');
      setPointsError('');
      setPointsLoading(false);
      return;
    }

    if (!selectedRally) {
      setRallyPoints([]);
      setSelectedRallyPoint('');
      setPointsError('');
      setPointsLoading(false);
      return;
    }

    const controlPointsSource = controlPointsUrlTemplate || rallyListUrl;
    const requestUrl = buildUrlWithTemplate(
      controlPointsSource,
      { rallyId: selectedRally, rally_id: selectedRally },
      { rally_id: selectedRally }
    );
    console.log(`[useTimeControlApi] Fetching control points. Rally: ${selectedRally}. URL: ${requestUrl}`);

    if (!requestUrl) {
      setRallyPoints([]);
      setSelectedRallyPoint('');
      setPointsLoading(false);
      setPointsError('Control point endpoint unavailable.');
      return;
    }

    let isActive = true;
    const controller = new AbortController();

    const fetchControlPoints = async () => {
      setPointsLoading(true);
      setPointsError('');
      try {
        const response = await fetch(requestUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (!isActive) return;
        console.log('[useTimeControlApi] Control points data:', data);
        const normalizedData = Array.isArray(data) ? data : [];
        setRallyPoints(normalizedData);
        setSelectedRallyPoint((prev) => {
          if (prev && normalizedData.some((item) => getOptionValue(item) === prev)) {
            return prev;
          }
          const defaultOption = normalizedData[0];
          return defaultOption ? getOptionValue(defaultOption) : '';
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Failed to load control points:', error);
        if (isActive) {
          setRallyPoints([]);
          setSelectedRallyPoint('');
          setPointsError('Failed to load control points.');
        }
      } finally {
        if (isActive) {
          setPointsLoading(false);
        }
      }
    };

    fetchControlPoints();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [selectedRally, timeControlEnabled, controlPointsUrlTemplate, rallyListUrl]);

  useEffect(() => {
    if (!timeControlEnabled) {
      setPointTimes([]);
      setTimesError('');
      setTimesLoading(false);
      return;
    }

    if (!selectedRally || !selectedRallyPoint) {
      setPointTimes([]);
      setTimesError('');
      setTimesLoading(false);
      return;
    }

    const requestUrl = buildUrlWithTemplate(
      timesUrlTemplate,
      {
        rallyId: selectedRally,
        rally_id: selectedRally,
        pointId: selectedRallyPoint,
        point_id: selectedRallyPoint,
      },
      { rally_id: selectedRally, point_id: selectedRallyPoint }
    );
    console.log(`[useTimeControlApi] Fetching times. Rally: ${selectedRally}, Point: ${selectedRallyPoint}. URL: ${requestUrl}`);

    if (!requestUrl) {
      setPointTimes([]);
      setTimesLoading(false);
      setTimesError('Time results endpoint unavailable.');
      return;
    }

    let isActive = true;
    let refreshTimerId;
    let currentController = null;

    async function fetchTimes({ showLoading = true } = {}) {
      if (!isActive) {
        return;
      }

      if (currentController) {
        currentController.abort();
      }

      const localController = new AbortController();
      currentController = localController;

      if (showLoading) {
        setTimesLoading(true);
        setTimesError('');
      }

      try {
        const response = await fetch(requestUrl, {
          signal: localController.signal,
          cache: 'no-cache',
        });
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (!isActive || currentController !== localController) {
          return;
        }
        console.log('[useTimeControlApi] Times data:', data);
        setPointTimes(Array.isArray(data) ? data : []);
        setTimesError('');
      } catch (error) {
        if (
          localController.signal.aborted ||
          !isActive ||
          currentController !== localController
        ) {
          return;
        }
        console.error('Failed to load time results:', error);
        setPointTimes([]);
        setTimesError('Failed to load times.');
      } finally {
        if (!isActive || currentController !== localController) {
          return;
        }
        setTimesLoading(false);
        scheduleNextRefresh();
      }
    }

    function scheduleNextRefresh() {
      if (!isActive) {
        return;
      }
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
      }
      const delay =
        Number.isFinite(refreshIntervalMs) && refreshIntervalMs > 0
          ? refreshIntervalMs
          : DEFAULT_TIME_CONTROL_CONFIG.refreshIntervalMs;
      refreshTimerId = setTimeout(() => {
        fetchTimes({ showLoading: false });
      }, delay);
    }

    fetchTimes({ showLoading: true });

    return () => {
      isActive = false;
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
      }
      if (currentController) {
        currentController.abort();
      }
    };
  }, [
    selectedRally,
    selectedRallyPoint,
    timeControlEnabled,
    timesUrlTemplate,
    refreshIntervalMs,
  ]);

  return {
    settings: timeControlSettings,
    rallyOptions,
    rallyLoading,
    rallyError,
    selectedRally,
    setSelectedRally,
    rallyPoints,
    pointsLoading,
    pointsError,
    selectedRallyPoint,
    setSelectedRallyPoint,
    pointTimes,
    timesLoading,
    timesError,
  };
};
